import React, { type CSSProperties } from "react";
import { Mail, MessageCircle, Phone } from "lucide-react";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import {
  buildSmsEnquiryHref,
  buildWhatsAppEnquiryHref,
  normalisePhoneNumberForSms,
} from "../../products/ProductInfoCard/ProductInfoCard.helpers";
import {
  serviceContactCategoryTitleTokens,
  type ServiceContactCategoryTitleIconToken,
  type ServiceContactCategoryTitlePatternToken,
} from "./ServiceContactCategoryTitle.tokens";

export type ServiceContactCategoryTitleContactIcon =
  | ServiceContactCategoryTitleIconToken
  | React.ReactNode;

export type ServiceContactCategoryTitleContactMethod =
  | "email"
  | "phone"
  | "sms"
  | "message"
  | "whatsapp"
  | "custom";

export type ServiceContactCategoryTitleTemplateValues = Record<
  string,
  string | number | boolean | null | undefined
>;

export type ServiceContactCategoryTitleContact = {
  id?: string | number;
  label: React.ReactNode;
  href?: string;
  method?: ServiceContactCategoryTitleContactMethod;
  phoneNumber?: string;
  email?: string;
  messageTemplate?: string;
  subjectTemplate?: string;
  ariaLabel?: string;
  title?: string;
  target?: string;
  rel?: string;
  icon?: ServiceContactCategoryTitleContactIcon;
};

export type ServiceContactCategoryTitleProps = {
  title?: React.ReactNode;
  ctaMessage?: React.ReactNode;
  contacts?: ServiceContactCategoryTitleContact[];
  titleComponent?: "h1" | "h2" | "h3" | "div";
  phoneNumber?: string;
  smsNumber?: string;
  whatsappNumber?: string;
  email?: string;
  businessName?: string;
  serviceName?: string;
  pageUrl?: string;
  templateValues?: ServiceContactCategoryTitleTemplateValues;
  responseTemplate?: string;
  smsResponseTemplate?: string;
  whatsappResponseTemplate?: string;
  emailSubjectTemplate?: string;
  emailResponseTemplate?: string;
  includePageUrlInResponse?: boolean;
  backgroundColor?: string;
  contrast?: boolean;
  textColor?: string;
  iconColor?: string;
  dividerColor?: string;
  pattern?: ServiceContactCategoryTitlePatternToken | false | "none";
  patternColor?: string;
  patternAccentColor?: string;
  patternOpacity?: number;
  maxWidth?: number | string;
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
  contactsAriaLabel?: string;
};

type CssVarStyle = CSSProperties &
  Record<`--${string}`, string | number | undefined>;

const t = serviceContactCategoryTitleTokens;

const defaultIconOrder: ServiceContactCategoryTitleIconToken[] = [
  "email",
  "phone",
  "message",
  "whatsapp",
];

const iconRegistry: Record<ServiceContactCategoryTitleIconToken, React.ElementType> = {
  email: Mail,
  phone: Phone,
  message: ChatBubbleLeftRightIcon,
  whatsapp: MessageCircle,
};

const iconMethodRegistry: Record<ServiceContactCategoryTitleIconToken, ServiceContactCategoryTitleContactMethod> = {
  email: "email",
  phone: "phone",
  message: "sms",
  whatsapp: "whatsapp",
};

const toCssSize = (value: number | string) =>
  typeof value === "number" ? `${value}px` : value;

const cleanValue = (value?: string) => value?.trim() || "";

const isIconToken = (
  icon: ServiceContactCategoryTitleContactIcon | undefined,
): icon is ServiceContactCategoryTitleIconToken =>
  typeof icon === "string" && icon in iconRegistry;

const normaliseContactMethod = (
  method?: ServiceContactCategoryTitleContactMethod,
) => (method === "message" ? "sms" : method);

const inferContactMethod = (
  contact: ServiceContactCategoryTitleContact,
  index: number,
): ServiceContactCategoryTitleContactMethod => {
  const explicitMethod = normaliseContactMethod(contact.method);

  if (explicitMethod) return explicitMethod;

  if (isIconToken(contact.icon)) return iconMethodRegistry[contact.icon];

  const href = cleanValue(contact.href).toLowerCase();

  if (href.startsWith("mailto:")) return "email";
  if (href.startsWith("tel:")) return "phone";
  if (href.startsWith("sms:")) return "sms";
  if (href.includes("wa.me/") || href.includes("whatsapp")) return "whatsapp";

  return iconMethodRegistry[defaultIconOrder[index % defaultIconOrder.length]];
};

const getTemplateValue = (
  values: ServiceContactCategoryTitleTemplateValues,
  key: string,
) => {
  const directValue = values[key];
  if (directValue !== undefined && directValue !== null) return String(directValue);
  return "";
};

const renderTemplate = (
  template: string,
  values: ServiceContactCategoryTitleTemplateValues,
) =>
  template.replace(
    /{{\s*([a-zA-Z0-9_.-]+)\s*}}|{\s*([a-zA-Z0-9_.-]+)\s*}/g,
    (_match, doubleBraceKey, singleBraceKey) =>
      getTemplateValue(values, doubleBraceKey || singleBraceKey),
  );

const appendPageUrl = (message: string, pageUrl: string) => {
  if (!pageUrl || message.includes(pageUrl)) return message;
  return `${message} ${pageUrl}`.trim();
};

const buildTelHref = (phoneNumber: string) => {
  const phone = normalisePhoneNumberForSms(phoneNumber);
  return phone ? `tel:${phone}` : "";
};

const buildMailtoHref = (email: string, subject: string, body: string) => {
  const safeEmail = cleanValue(email);

  if (!safeEmail) return "";

  const params = new URLSearchParams();
  if (subject) params.set("subject", subject);
  if (body) params.set("body", body);

  const query = params.toString();
  return `mailto:${safeEmail}${query ? `?${query}` : ""}`;
};

const getEmailFromHref = (href?: string) => {
  const safeHref = cleanValue(href);

  if (!safeHref.toLowerCase().startsWith("mailto:")) return "";

  return safeHref.slice("mailto:".length).split("?")[0];
};

const getPhoneFromHref = (href?: string) => {
  const safeHref = cleanValue(href);
  const lowerHref = safeHref.toLowerCase();

  if (lowerHref.startsWith("tel:")) {
    return safeHref.slice("tel:".length).split("?")[0];
  }

  if (lowerHref.startsWith("sms:")) {
    return safeHref.slice("sms:".length).split("?")[0];
  }

  const whatsAppMatch = safeHref.match(/wa\.me\/([^?/#]+)/i);
  if (whatsAppMatch?.[1]) return whatsAppMatch[1];

  const whatsAppUrlMatch = safeHref.match(/[?&]phone=([^&#]+)/i);
  if (whatsAppUrlMatch?.[1]) return decodeURIComponent(whatsAppUrlMatch[1]);

  return "";
};

const resolvePattern = (
  pattern: ServiceContactCategoryTitleProps["pattern"],
): ServiceContactCategoryTitlePatternToken | "none" => {
  if (!pattern || pattern === "none") return "none";
  return pattern;
};

const renderIcon = (
  icon: ServiceContactCategoryTitleContactIcon | undefined,
  index: number,
) => {
  const fallbackIcon = defaultIconOrder[index % defaultIconOrder.length];
  const resolvedIcon = icon ?? fallbackIcon;

  if (isIconToken(resolvedIcon)) {
    const Icon = iconRegistry[resolvedIcon];
    return <Icon aria-hidden="true" />;
  }

  return resolvedIcon;
};

const defaultContacts: ServiceContactCategoryTitleContact[] =
  t.defaults.contacts.map((contact) => ({ ...contact }));

const ServiceContactCategoryTitle = ({
  title = t.defaults.title,
  ctaMessage = t.defaults.ctaMessage,
  contacts = defaultContacts,
  titleComponent = "h2",
  phoneNumber,
  smsNumber,
  whatsappNumber,
  email,
  businessName,
  serviceName,
  pageUrl,
  templateValues,
  responseTemplate,
  smsResponseTemplate,
  whatsappResponseTemplate,
  emailSubjectTemplate,
  emailResponseTemplate,
  includePageUrlInResponse = true,
  backgroundColor = t.colors.background,
  contrast = true,
  textColor,
  iconColor,
  dividerColor,
  pattern = t.defaults.pattern,
  patternColor,
  patternAccentColor,
  patternOpacity,
  maxWidth = t.sizes.maxWidth,
  className = "",
  style,
  ariaLabel,
  contactsAriaLabel = "Contact options",
}: ServiceContactCategoryTitleProps) => {
  const patternName = resolvePattern(pattern);
  const patternTokens =
    patternName === "none" ? t.patterns.angled : t.patterns[patternName];
  const baseTextColor = contrast ? t.colors.contrastText : t.colors.standardText;
  const resolvedTextColor = textColor || baseTextColor;
  const resolvedIconColor = iconColor || resolvedTextColor;
  const resolvedDividerColor =
    dividerColor || (contrast ? t.colors.contrastDivider : t.colors.standardDivider);
  const resolvedPatternColor =
    patternColor || (contrast ? t.colors.contrastPattern : t.colors.standardPattern);
  const resolvedPatternAccentColor =
    patternAccentColor ||
    (contrast ? t.colors.contrastPatternAccent : t.colors.standardPatternAccent);
  const resolvedPatternOpacity = patternOpacity;
  const TitleTag = titleComponent;
  const titleText = typeof title === "string" ? title : "";
  const serviceLabel = cleanValue(serviceName || titleText);
  const safePageUrl = cleanValue(pageUrl);
  const displayPhone = cleanValue(phoneNumber);
  const displaySmsNumber = cleanValue(smsNumber || displayPhone);
  const displayWhatsAppNumber = cleanValue(whatsappNumber || displayPhone);
  const displayEmail = cleanValue(email);
  const defaultResponseTemplate =
    responseTemplate ||
    (serviceLabel ? t.defaults.responseTemplate : t.defaults.fallbackResponseTemplate);
  const values: ServiceContactCategoryTitleTemplateValues = {
    businessName,
    serviceName: serviceLabel,
    categoryTitle: titleText,
    title: titleText,
    pageUrl: safePageUrl,
    phone: displayPhone,
    phoneNumber: displayPhone,
    smsNumber: displaySmsNumber,
    whatsappNumber: displayWhatsAppNumber,
    email: displayEmail,
    ...templateValues,
  };

  const buildResponse = (template?: string) => {
    const rendered = renderTemplate(template || defaultResponseTemplate, values).trim();
    return includePageUrlInResponse ? appendPageUrl(rendered, safePageUrl) : rendered;
  };

  const buildContactHref = (
    contact: ServiceContactCategoryTitleContact,
    index: number,
  ) => {
    const method = inferContactMethod(contact, index);
    const fallbackPhone = getPhoneFromHref(contact.href);
    const contactPhone = cleanValue(contact.phoneNumber || fallbackPhone);

    if (method === "email") {
      const address = cleanValue(contact.email || displayEmail || getEmailFromHref(contact.href));
      const subjectTemplate =
        contact.subjectTemplate ||
        emailSubjectTemplate ||
        (serviceLabel ? t.defaults.emailSubjectTemplate : "Website enquiry");
      const subject = renderTemplate(subjectTemplate, values).trim() || "Website enquiry";
      const body = buildResponse(contact.messageTemplate || emailResponseTemplate);
      return buildMailtoHref(address, subject, body) || contact.href || "";
    }

    if (method === "sms") {
      const smsPhone = cleanValue(contactPhone || displaySmsNumber);
      const message = buildResponse(contact.messageTemplate || smsResponseTemplate);
      return buildSmsEnquiryHref(smsPhone, message) || contact.href || "";
    }

    if (method === "whatsapp") {
      const whatsAppPhone = cleanValue(contactPhone || displayWhatsAppNumber);
      const message = buildResponse(contact.messageTemplate || whatsappResponseTemplate);
      return buildWhatsAppEnquiryHref(whatsAppPhone, message) || contact.href || "";
    }

    if (method === "phone") {
      const callPhone = cleanValue(contactPhone || displayPhone);
      return buildTelHref(callPhone) || contact.href || "";
    }

    return contact.href || "";
  };

  const rootStyle: CssVarStyle = {
    "--scct-bg": backgroundColor,
    "--scct-text": resolvedTextColor,
    "--scct-icon": resolvedIconColor,
    "--scct-divider": resolvedDividerColor,
    "--scct-pattern-color": resolvedPatternColor,
    "--scct-pattern-accent": resolvedPatternAccentColor,
    "--scct-pattern-dot-size": `${patternTokens.dotSize}px`,
    "--scct-pattern-dot-opacity":
      resolvedPatternOpacity ?? patternTokens.dotOpacity,
    "--scct-pattern-band-opacity":
      resolvedPatternOpacity ?? patternTokens.bandOpacity,
    "--scct-max-width": toCssSize(maxWidth),
    ...style,
  };

  const sectionAriaLabel =
    ariaLabel || (typeof title === "string" ? title : undefined);

  return (
    <section
      data-component="service-contact-category-title"
      data-contrast={contrast ? "true" : "false"}
      data-pattern={patternName}
      className={`service-contact-category-title ${className}`.trim()}
      style={rootStyle}
      aria-label={sectionAriaLabel}
    >
      <div className="service-contact-category-title-shell">
        <div className="service-contact-category-title-header">
          {title ? (
            <TitleTag className="service-contact-category-title-heading">
              {title}
            </TitleTag>
          ) : null}

          {title && ctaMessage ? (
            <span
              className="service-contact-category-title-divider"
              aria-hidden="true"
            />
          ) : null}

          {ctaMessage ? (
            <p className="service-contact-category-title-cta">{ctaMessage}</p>
          ) : null}
        </div>

        {contacts.length ? (
          <nav
            className="service-contact-category-title-contacts"
            aria-label={contactsAriaLabel}
          >
            {contacts.map((contact, index) => {
              const href = buildContactHref(contact, index);
              const method = inferContactMethod(contact, index);
              const target =
                contact.target ?? (method === "whatsapp" ? "_blank" : undefined);
              const rel =
                contact.rel ?? (method === "whatsapp" ? "noopener noreferrer" : undefined);
              const key = contact.id ?? `${index}-${href || ""}`;
              const content = (
                <>
                  <span
                    className="service-contact-category-title-contact-icon"
                    aria-hidden="true"
                  >
                    {renderIcon(contact.icon, index)}
                  </span>
                  <span className="service-contact-category-title-contact-label">
                    {contact.label}
                  </span>
                </>
              );

              if (!href) {
                return (
                  <span
                    key={key}
                    className="service-contact-category-title-contact"
                    title={contact.title}
                  >
                    {content}
                  </span>
                );
              }

              return (
                <a
                  key={key}
                  className="service-contact-category-title-contact"
                  href={href}
                  aria-label={contact.ariaLabel}
                  title={contact.title}
                  target={target}
                  rel={rel}
                >
                  {content}
                </a>
              );
            })}
          </nav>
        ) : null}
      </div>
    </section>
  );
};

export {
  serviceContactCategoryTitleTokens,
  type ServiceContactCategoryTitleIconToken,
  type ServiceContactCategoryTitlePatternToken,
};

export default ServiceContactCategoryTitle;
